import { SITE_URL, defaultSeo, pageTitle, type PageSeo } from '../content/seo';
import { brand } from '../content/site';

export interface SeoProps extends PageSeo {
  /** Sayfanın kök göreli adresi (`/fiyatlar`, `/blog?sayfa=2`): canonical ve `og:url`. `noindex` sayfada verilmez. */
  path?: string;
}

/** Kök göreli görsel yolu site adresiyle tamamlanır; CMS'ten gelen tam adres olduğu gibi kalır. */
const absolute = (src: string) => (/^https?:\/\//.test(src) ? src : `${SITE_URL}${src}`);

/**
 * Sayfa başına arama motoru ve paylaşım etiketleri. React 19, `<title>`, `<meta>` ve `<link>`'i ağacın neresinde
 * çizilirse çizilsin `<head>`'e taşır (başlığı en başa koyar); ek kütüphane gerekmez. Sayfa başına TEK `Seo`
 * çizilir: kabuk (`SubpageShell`/`PageShell`) adresten bulur, ana sayfa ve 404 kendisi çizer; iki `<title>`
 * olursa tarayıcı ilkini alır. Site SPA olduğundan etiketler JavaScript çalışınca oluşur; JavaScript
 * çalıştırmayan paylaşım botları için build sonrası ön-çizim ayrı adımdır (ILERLEME → SEO 2).
 * Yapılandırılmış veri (`jsonLd`) gövdede kalır; Google için yeri fark etmez. JSON içindeki `<`, metin
 * `</script>` içerse bile etiketi kapatmasın diye kaçırılır (ön-çizimde HTML'e yazılacak).
 */
export function Seo({ title, description, path, noindex, image, imageSize, type = 'website', article, jsonLd }: SeoProps) {
  const fullTitle = pageTitle(title);
  const url = path === undefined ? null : `${SITE_URL}${path}`;
  const img = absolute(image ?? defaultSeo.image);
  const size = image ? imageSize : defaultSeo.imageSize;
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex, follow" /> : url && <link rel="canonical" href={url} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={brand.name} />
      <meta property="og:locale" content="tr_TR" />
      {url && !noindex && <meta property="og:url" content={url} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={img} />
      {size && <meta property="og:image:width" content={String(size.width)} />}
      {size && <meta property="og:image:height" content={String(size.height)} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {article && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
      {article?.author && <meta property="article:author" content={article.author} />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd).replace(/</g, '\\u003c')}</script>}
    </>
  );
}
